import { Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { ReactNode, useMemo } from "react";
import { HeatOverrideOption, HeatQueueEntry } from "src/store/api/generatedApi";

export interface HeatRow {
  kind: "heat";
  id: number;
  heatId: number;
  stableHeatId: string;
  grade: string;
  productGroup: string | null;
  recipeName: string | null;
  status: HeatQueueEntry["status"];
  heatStartTime: string | null;
  overrideId: number | null;
}

export const toHeatRow = (heat: HeatQueueEntry): HeatRow => ({
  kind: "heat",
  id: heat.heat_id,
  heatId: heat.heat_id,
  stableHeatId: heat.stable_heat_id,
  grade: heat.steel_grade,
  productGroup: heat.product_group,
  recipeName: heat.recipe?.name ?? heat.recipe_name,
  status: heat.status,
  heatStartTime: heat.heat_start_time,
  overrideId: heat.override_id,
});

export type HeatCell<Row extends GridValidRowModel> = (
  render: (row: HeatRow) => ReactNode
) => (params: GridRenderCellParams<Row>) => ReactNode;

export const HEAT_GRID_SX = {
  border: 1,
  borderColor: "grey.300",
  "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
} as const;

interface StartTimeColumn {
  format: string;
  width: number;
}

export const QUEUE_START_TIME: StartTimeColumn = {
  format: "HH:mm",
  width: 120,
};
export const COMPLETED_START_TIME: StartTimeColumn = {
  format: "L LT",
  width: 170,
};

export const useHeatDataColumns = <Row extends GridValidRowModel>({
  heatCell,
  startTime,
}: {
  heatCell: HeatCell<Row>;
  startTime: StartTimeColumn;
}): GridColDef<Row>[] => {
  const { t } = useTenantTranslation();
  const { heat_override_options: heatOverrideOptions } = useTenantData();
  const { toLocalDatetime } = useLocalDatetime();

  return useMemo(() => {
    const optionalColumn = (
      option: HeatOverrideOption,
      definition: GridColDef<Row>
    ): GridColDef<Row>[] =>
      heatOverrideOptions.includes(option) ? [definition] : [];

    return [
      {
        field: "heatId",
        headerName: t("heat"),
        width: 100,
        type: "number",
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2Mono">{row.heatId}</Typography>
        )),
      },
      ...optionalColumn("stable_heat_id_column", {
        field: "stableHeatId",
        headerName: t("stableHeatId"),
        width: 140,
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2Mono">{row.stableHeatId}</Typography>
        )),
      }),
      {
        field: "heatStartTime",
        headerName: t("start"),
        width: startTime.width,
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {row.heatStartTime === null
              ? "—"
              : toLocalDatetime(row.heatStartTime, startTime.format)}
          </Typography>
        )),
      },
      {
        field: "grade",
        headerName: t("steelGrade"),
        width: 120,
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2">{row.grade}</Typography>
        )),
      },
      {
        field: "recipeName",
        headerName: t("recipe"),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2">{row.recipeName ?? "—"}</Typography>
        )),
      },
      ...optionalColumn("product_group_column", {
        field: "productGroup",
        headerName: t("group"),
        width: 140,
        sortable: false,
        renderCell: heatCell((row) => (
          <Typography variant="body2">{row.productGroup ?? "—"}</Typography>
        )),
      }),
    ];
  }, [t, heatCell, startTime, toLocalDatetime, heatOverrideOptions]);
};
